/**
 * @file useSellerInfo.ts
 * @description Сведения о продавце для юридической строки сайта (phase19.16
 * Р1; SB19, SB59; uiux-design-r1 §2 «Всегда в подвале», §4 «Продавец»).
 *
 * Только чтение: источник — основной активный профиль «Реквизитов» и почта
 * из «Настройки → Магазин». Чего не хватает для публикации — список полей
 * (`missing`); ту же проверку делает сервер при публикации (SB59).
 */

import { useCallback, useEffect, useState } from "react";

import { listLegalProfiles, type LegalProfileDto } from "../../lib/api-crm";

export interface SellerInfo {
  profile: LegalProfileDto | null;
  /** Профилей несколько, основной не выбран. */
  noDefault: boolean;
  /** «ОГРН» или «ОГРНИП» — по длине ИНН (12 цифр — ИП). */
  ogrnLabel: "ОГРН" | "ОГРНИП";
  /** Названия незаполненных полей: «ОГРНИП», «юридический адрес». */
  missing: string[];
}

export interface SellerInfoState {
  info: SellerInfo | null;
  loading: boolean;
  error: boolean;
  reload: () => void;
}

/** Сведения и незаполненные поля — чистая функция (проверяется тестом). */
export function sellerInfo(profiles: LegalProfileDto[], shopEmail: string | null): SellerInfo {
  const active = profiles.filter((p) => p.isActive);
  const profile = active.find((p) => p.isDefault) ?? (active.length === 1 ? active[0]! : null);
  const ogrnLabel = profile && profile.inn.replace(/\D/g, "").length === 12 ? "ОГРНИП" : "ОГРН";
  const missing: string[] = [];
  if (!profile) {
    missing.push("основной профиль реквизитов");
  } else {
    if (!profile.legalName.trim()) missing.push("наименование");
    if (!profile.inn.trim()) missing.push("ИНН");
    if (!profile.ogrn?.trim()) missing.push(ogrnLabel);
    if (!profile.legalAddress.trim()) missing.push("юридический адрес");
    if (!profile.phone?.trim()) missing.push("телефон");
  }
  if (!shopEmail?.trim()) missing.push("почта магазина");
  return { profile, noDefault: !profile && active.length > 1, ogrnLabel, missing };
}

export function useSellerInfo(shopEmail: string | null): SellerInfoState {
  const [profiles, setProfiles] = useState<LegalProfileDto[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    listLegalProfiles()
      .then((res) => !cancelled && setProfiles(res.items))
      .catch(() => !cancelled && setError(true))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [attempt]);

  const reload = useCallback(() => setAttempt((a) => a + 1), []);
  return { info: profiles ? sellerInfo(profiles, shopEmail) : null, loading, error, reload };
}
